import { useId, useMemo, useRef, useState } from "react";
import type { ReactNode, SelectHTMLAttributes } from "react";
import { Icon } from "./icons";

// Select — codified from 08-formlar-girisler.html "Seçim ve arama". A styled NATIVE
// <select> (keeps the platform's a11y + option semantics) wearing the founder's
// trigger chrome + chevron. Copy/options are the caller's.
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  hint?: ReactNode;
  fieldClassName?: string;
}

export function Select({
  label,
  hint,
  fieldClassName,
  className,
  id,
  children,
  ...select
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const hintId = `${selectId}-hint`;
  return (
    <div className={`k-field${fieldClassName ? ` ${fieldClassName}` : ""}`}>
      <label className="k-field__label" htmlFor={selectId}>
        {label}
      </label>
      <div className="k-select">
        <select
          id={selectId}
          className={`k-select__native${className ? ` ${className}` : ""}`}
          aria-describedby={hint ? hintId : undefined}
          {...select}
        >
          {children}
        </select>
        <Icon name="chevron-down" size={16} strokeWidth={2} className="k-select__chevron" />
      </div>
      {hint ? (
        <span id={hintId} className="k-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

// SearchInput — the "Hane ara" search field (search glyph + calm panel fill).
export interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label?: ReactNode;
  fieldClassName?: string;
}

export function SearchInput({ label, fieldClassName, id, ...input }: SearchInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={`k-field${fieldClassName ? ` ${fieldClassName}` : ""}`}>
      {label ? (
        <label className="k-field__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className="k-search">
        <Icon name="search" size={16} strokeWidth={2} />
        <input id={inputId} type="search" {...input} />
      </div>
    </div>
  );
}

export interface SearchSelectOption {
  value: string;
  label: string;
}

export interface SearchSelectProps {
  label?: ReactNode;
  value: string | null;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  fieldClassName?: string;
}

// SearchSelect — the searchable dropdown from 08 (trigger + popover menu with a live
// filter and the green check on the selected row). Fully controlled + props-driven.
export function SearchSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "—",
  searchPlaceholder,
  fieldClassName,
}: SearchSelectProps) {
  const autoId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listId = `${autoId}-list`;
  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={`k-field${fieldClassName ? ` ${fieldClassName}` : ""}`}>
      {label ? (
        <label className="k-field__label" htmlFor={autoId}>
          {label}
        </label>
      ) : null}
      <div
        className="k-combo"
        ref={containerRef}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
        }}
      >
        <button
          id={autoId}
          type="button"
          className="k-combo__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((o) => !o)}
        >
          <span>{selected ? selected.label : placeholder}</span>
          <Icon
            name={open ? "chevron-up" : "chevron-down"}
            size={16}
            strokeWidth={2}
            style={{ color: "var(--k-blue)" }}
          />
        </button>
        {open ? (
          <div className="k-combo__menu" id={listId} role="listbox">
            {searchPlaceholder !== undefined ? (
              <input
                className="k-input"
                style={{ marginBottom: 6 }}
                autoFocus
                type="search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            ) : null}
            {filtered.map((o) => {
              const isSel = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  className={`k-combo__opt${isSel ? " k-combo__opt--selected" : ""}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  {o.label}
                  {isSel ? (
                    <Icon
                      name="check"
                      size={15}
                      strokeWidth={2.4}
                      style={{ color: "var(--k-green-solid)" }}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
